import React, { useState } from "react";
import { validateEmail } from "../../../utils/validationUtils";
import { API_ENDPOINTS, apiClient } from "../../../api";
import img2 from "../../../images/newsletter-couple.webp";
import img1 from "../../../images/newsletter-road.webp";
import img from "../../../images/newsletter-hero.webp";

function NewsletterBanner() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setAlreadySubscribed(false);

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      const data = await apiClient.post(API_ENDPOINTS.NEWSLETTER_SUBSCRIPTION, {
        email: email,
      });

      // 200 OK — email is already subscribed and confirmed
      if (data.message === "Email is already subscribed") {
        setAlreadySubscribed(true);
        return;
      }

      // 201 Created — subscription initiated, confirmation email sent
      // Bharti's response uses `confirmed` (not `subscribed`) — checking `success` is enough
      if (data.success === true) {
        setSubmitted(true);
        return;
      }

      // 400 — missing/invalid email or any other failure
      throw new Error(data.message || "Unable to subscribe at the moment.");

    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Something went wrong.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 bg-white font-sans selection:bg-emerald-100">
      {/* Main Structural Visual Grid Container Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 rounded-[24px] border border-gray-200/60 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] overflow-hidden lg:h-[490px]">
        
        {/* Left Side: Wide-aspect Image Column */}
        <div className="relative bg-gray-100 h-[340px] lg:h-full w-full overflow-hidden flex-shrink-0">
          <img
            src={img}
            alt="Scenic coastal drive"
            className="w-full h-full object-cover object-center"
          />

          {/* Floating Top-Left Tag */}
          <div className="absolute top-4 left-4 bg-white px-3.5 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm border border-gray-100/50">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#10b981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-[11.5px] font-bold text-gray-800 tracking-wide">Exclusive deals inside</span>
          </div>

          {/* Overlapping Thumbnails Stacked cleanly on Bottom-Right */}
          <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-10">
            <div className="w-[92px] h-[92px] rounded-xl overflow-hidden border-2 border-white shadow-md">
              <img src={img1} alt="Travelers" className="w-full h-full object-cover" />
            </div>
            <div className="w-[92px] h-[92px] rounded-xl overflow-hidden border-2 border-white shadow-md">
              <img src={img2} alt="Scenic drive road" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Bottom-Left Overlay Banner Card */}
          <div className="absolute bottom-4 left-4 bg-white max-w-[230px] p-4 rounded-2xl shadow-sm border border-gray-100/40">
            <span className="text-[9px] font-bold text-[#10b981] uppercase tracking-wider block mb-0.5">
              Curated for travellers
            </span>
            <h4 className="text-[14px] font-bold text-gray-900 leading-snug">
              Scenic drives & city escapes
            </h4>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Fresh routes every month
            </p>
          </div>
        </div>

        {/* Right Side: Form & Text Content Column */}
        <div className="bg-white p-8 lg:p-10 flex flex-col justify-between h-full">
          
          {/* Top Info Header Wrapper */}
          <div>
            {/* Newsletter Tag Label */}
            <div className="inline-flex items-center gap-1 bg-[#e6fcf2] text-primary px-2 py-0.5 rounded mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.003 6.003 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="text-[9.5px] font-bold uppercase tracking-widest translate-y-[0.5px]">Newsletter</span>
            </div>

            {/* Main Title Heading */}
            <h2 className="text-[28px] lg:text-[32px] font-bold text-[#0f172a] tracking-tight leading-tight mb-3">
              Join our <span className="text-primary">Exclusive</span> Newsletter
            </h2>
            
            <p className="text-[13.5px] text-text-muted font-normal leading-relaxed mb-5 max-w-md">
              Get the best car rental deals in your inbox. No spam — just executive savings.
            </p>

            {/* Custom List Rows */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-[13px] text-[#1e293b] font-medium">
                <span className="w-5 h-5 rounded bg-[#e6fcf2] flex items-center justify-center flex-shrink-0 text-[#10b981]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zM9 16l6-6M9 10l6 6" />
                  </svg>
                </span>
                Exclusive car rental deals & member-only rates
              </div>

              <div className="flex items-center gap-3 text-[13px] text-[#1e293b] font-medium">
                <span className="w-5 h-5 rounded bg-[#e6fcf2] flex items-center justify-center flex-shrink-0 text-[#10b981]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </span>
                Hand-picked offers from our deals team
              </div>

              <div className="flex items-center gap-3 text-[13px] text-[#1e293b] font-medium">
                <span className="w-5 h-5 rounded bg-[#e6fcf2] flex items-center justify-center flex-shrink-0 text-[#10b981]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.003 6.003 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </span>
                No spam — just executive savings
              </div>
            </div>
          </div>

          {/* Bottom Form Action Component wrapper */}
          <div className="mt-6">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="w-full max-w-lg">
                <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 bg-white rounded-xl border p-2 transition-all ${
                  error ? "border-red-400 ring-1 ring-red-400/20" : "border-gray-200 focus-within:border-[#10b981]/60 focus-within:ring-1 focus-within:ring-[#10b981]/20"
                }`}>
                  
                  {/* Embedded input metadata box */}
                  <div className="flex flex-col flex-grow px-2 py-0.5">
                    <label className="text-[10px] font-bold text-gray-800 uppercase tracking-wide">
                      Enter your email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError(null);
                      }}
                      placeholder="you@email.com"
                      className="w-full text-[13.5px] text-gray-800 font-normal placeholder-gray-400 bg-transparent border-0 p-0 mt-0.5 outline-none focus:ring-0 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Call-To-Action Button */}
                  <button 
                    type="submit"
                    disabled={isLoading}
                    className="bg-primary hover:bg-[#059669] disabled:opacity-60 text-white text-[13.5px] font-semibold px-5 py-2.5 rounded-lg flex items-center justify-center gap-1 shadow-sm transition-colors whitespace-nowrap active:scale-[0.99]"
                  >
                    {isLoading ? "Subscribing..." : "Subscribe Now"}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>

                {/* Inline feedback messages — split so errors are red, soft info is green */}
                {error && (
                  <p className="mt-2 text-xs text-red-500 font-medium pl-1">{error}</p>
                )}
                {alreadySubscribed && !error && (
                  <p className="mt-2 text-xs text-green-500 font-medium pl-1">
                    You're already signed up! Stay tuned for more deals.
                  </p>
                )}
              </form>
            ) : (
              /* Success Callback Panel */
              <div className="rounded-xl bg-[#e6fcf2] border border-[#10b981]/20 p-4 max-w-lg text-left animate-in fade-in zoom-in-95 duration-300">
                <p className="text-[#10b981] font-bold text-sm tracking-wide">
                  Welcome aboard!
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Please check your inbox to confirm your subscription.
                </p>
              </div>
            )}

            {/* Bottom Gray footer disclaimer line link */}
            <p className="text-[11px] text-gray-400 mt-3 pl-1">
              By subscribing, you agree to our{" "}
              <a href="/policy" className="text-[#10b981] hover:underline font-medium">
                Privacy Policy
              </a>
              .
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

export default NewsletterBanner;